/**
 * Badge Grid Component
 * Displays all badges in a grid, optionally grouped by category
 */

'use client';

import BadgeDisplay, { Badge } from './BadgeDisplay';

interface BadgeGridProps {
  badges: Badge[];
  groupByCategory?: boolean;
  showUnearned?: boolean;
  maxDisplay?: number;
  size?: 'sm' | 'md' | 'lg';
}

const categoryLabels: Record<string, string> = {
  reading: 'Reading',
  tools: 'Tool Usage',
  streak: 'Streaks',
  learning: 'Learning',
  engagement: 'Engagement',
};

const categoryOrder = ['reading', 'tools', 'streak', 'learning', 'engagement'];

export default function BadgeGrid({
  badges,
  groupByCategory = false,
  showUnearned = true,
  maxDisplay,
  size = 'md',
}: BadgeGridProps) {
  // Filter badges based on showUnearned
  const filteredBadges = showUnearned ? badges : badges.filter((b) => b.earned);

  // Apply max display limit
  const displayBadges = maxDisplay ? filteredBadges.slice(0, maxDisplay) : filteredBadges;

  if (groupByCategory) {
    // Group badges by category
    const grouped = displayBadges.reduce(
      (acc, badge) => {
        if (!acc[badge.category]) acc[badge.category] = [];
        acc[badge.category].push(badge);
        return acc;
      },
      {} as Record<string, Badge[]>
    );

    return (
      <div className="space-y-6">
        {categoryOrder.map((category) => {
          const categoryBadges = grouped[category];
          if (!categoryBadges || categoryBadges.length === 0) return null;

          return (
            <div key={category}>
              <h4 className="text-sm font-medium text-foreground-secondary mb-3">
                {categoryLabels[category]}
              </h4>
              <div className="flex flex-wrap gap-3">
                {categoryBadges.map((badge) => (
                  <BadgeDisplay
                    key={badge.id}
                    badge={badge}
                    size={size}
                    showProgress
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Simple grid without grouping
  return (
    <div className="flex flex-wrap gap-3">
      {displayBadges.map((badge) => (
        <BadgeDisplay
          key={badge.id}
          badge={badge}
          size={size}
          showProgress
        />
      ))}
    </div>
  );
}
